import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ToolCase, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useToolboxes } from '@/hooks';
import { RenderIf } from '@/components/RenderIf';
import type { ToolboxFilterProps } from './types';
import { FilterButton } from './styled';

const ToolboxFilter = ({ agentId }: ToolboxFilterProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { data: toolboxes = [], isLoading } = useToolboxes({ agentId });

    const selectedToolboxId = searchParams.get('toolbox');

    const selectedToolbox = useMemo(() => {
        if (!selectedToolboxId) return null;
        return toolboxes.find(tb => tb.id === selectedToolboxId);
    }, [selectedToolboxId, toolboxes]);

    const handleSelectToolbox = ({ toolboxId }: { toolboxId: string }) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('toolbox', toolboxId);
        setSearchParams(newParams);
    };

    const handleClearFilter = () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('toolbox');
        setSearchParams(newParams);
    };

    return (
        <DropdownMenu>
            <FilterButton>
                <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="gap-2">
                        <ToolCase size={16} />
                        <RenderIf isTrue={!selectedToolbox}>
                            <span>Filter by Toolbox</span>
                        </RenderIf>
                        <RenderIf isTrue={!!selectedToolbox}>
                            <span className="text-muted-foreground">Toolbox:</span>
                            <span>{selectedToolbox?.name}</span>
                        </RenderIf>
                    </Button>
                </DropdownMenuTrigger>
            </FilterButton>
            <DropdownMenuContent align="end" className="w-80">
                <RenderIf isTrue={isLoading}>
                    <DropdownMenuItem disabled>Loading toolboxes...</DropdownMenuItem>
                </RenderIf>

                <RenderIf isTrue={!isLoading && toolboxes.length === 0}>
                    <DropdownMenuItem disabled>No toolboxes</DropdownMenuItem>
                </RenderIf>

                <RenderIf isTrue={!isLoading && toolboxes.length > 0}>
                    {toolboxes.map(toolbox => {
                        const isSelected = selectedToolboxId === toolbox.id;
                        const toolCount = toolbox.tools?.length || 0;
                        return (
                            <DropdownMenuItem
                                key={toolbox.id}
                                onClick={() =>
                                    handleSelectToolbox({
                                        toolboxId: toolbox.id,
                                    })
                                }
                                className="gap-2"
                            >
                                <ToolCase size={14} />
                                <span className="flex-1">
                                    {toolbox.name} ({toolCount})
                                </span>
                                <RenderIf isTrue={isSelected}>
                                    <Check size={14} />
                                </RenderIf>
                            </DropdownMenuItem>
                        );
                    })}

                    <RenderIf isTrue={!!selectedToolboxId}>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleClearFilter} className="gap-2">
                            <X size={14} />
                            Clear Filter
                        </DropdownMenuItem>
                    </RenderIf>
                </RenderIf>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default ToolboxFilter;
